import { QueryClient, QueryKey } from '@tanstack/react-query';

import { ANNOTATIONS_BASE_QUERY_KEY } from 'api/annotationAssignments';
import { AnnotationAssignment } from 'interfaces/annotation';

type AnnotationAssignmentsQueryData =
  | AnnotationAssignment[]
  | {
      annotationAssignments?: AnnotationAssignment[];
      [key: string]: unknown;
    }
  | undefined;

export type AnnotationAssignmentsQuerySnapshot = [QueryKey, AnnotationAssignmentsQueryData];

const isOriginsAnnotationAssignmentsQueryKey = (queryKey: QueryKey) => {
  const [firstSegment, secondSegment, thirdSegment] = queryKey as unknown[];

  return (
    (firstSegment === 'nucleaiOrigins' && secondSegment === 'annotationAssignments') ||
    (firstSegment === 'nucleaiOrigins' &&
      secondSegment === 'runExperimentTraining' &&
      thirdSegment === 'annotationAssignments')
  );
};

export const isAnnotationAssignmentsQueryKey = (queryKey: QueryKey) => {
  const [firstSegment] = queryKey as unknown[];

  return firstSegment === ANNOTATIONS_BASE_QUERY_KEY || isOriginsAnnotationAssignmentsQueryKey(queryKey);
};

const patchAnnotationAssignmentsQueryData = (
  queryData: AnnotationAssignmentsQueryData,
  updateAssignment: (assignment: AnnotationAssignment) => AnnotationAssignment
): AnnotationAssignmentsQueryData => {
  if (Array.isArray(queryData)) {
    return queryData.map(updateAssignment);
  }

  if (queryData?.annotationAssignments && Array.isArray(queryData.annotationAssignments)) {
    return {
      ...queryData,
      annotationAssignments: queryData.annotationAssignments.map(updateAssignment),
    };
  }

  return queryData;
};

export const captureAnnotationAssignmentsQuerySnapshots = (queryClient: QueryClient) =>
  queryClient.getQueriesData<AnnotationAssignmentsQueryData>({
    predicate: (query) => isAnnotationAssignmentsQueryKey(query.queryKey),
  });

export const restoreAnnotationAssignmentsQuerySnapshots = (
  queryClient: QueryClient,
  snapshots: AnnotationAssignmentsQuerySnapshot[]
) => {
  snapshots.forEach(([queryKey, queryData]) => {
    queryClient.setQueryData(queryKey, queryData);
  });
};

export const updateAnnotationAssignmentsAcrossCaches = (
  queryClient: QueryClient,
  updateAssignment: (assignment: AnnotationAssignment) => AnnotationAssignment
) => {
  queryClient.setQueriesData<AnnotationAssignmentsQueryData>(
    {
      predicate: (query) => isAnnotationAssignmentsQueryKey(query.queryKey),
    },
    (queryData) => patchAnnotationAssignmentsQueryData(queryData, updateAssignment)
  );
};

export const invalidateAnnotationAssignmentsAcrossCaches = (queryClient: QueryClient) => {
  void queryClient.invalidateQueries({
    queryKey: [ANNOTATIONS_BASE_QUERY_KEY],
    exact: false,
  });
  void queryClient.invalidateQueries({
    predicate: (query) => isOriginsAnnotationAssignmentsQueryKey(query.queryKey),
  });
  void queryClient.invalidateQueries({
    queryKey: ['annotations'],
    exact: false,
  });
};
